import _ from 'lodash';
import {compileContent, safeSubstract, safeAdd} from 'common/utils';
import RULES from 'framework/validation/evalRules';
import {
  ACTUAL_TOTAL,
  SCOPE_OF_WORK_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  DISALLOWED_TOTAL,
  CATEGORIZED_SUM,
  COMMITMENT_STATE,
} from 'common/requestDetails/constants';
import {calcCloseoutReportBudgetShare} from 'common/requestDetails/budgetCalculation';
import {computeBudgetSummary} from 'common/requestDetails/utils';

const unSetIf = (state, dataPath, args) => {
  const rule = _.get(args, 'rule');
  const isValid = RULES['evalRuleSet'][rule](state, _.get(args, 'props'));
  if (isValid) {
    _.unset(state, dataPath);
  }
};

const unSetMultipleIf = (state, dataPaths, args) => {
  const rule = _.get(args, 'rule');
  const isValid = RULES['evalRuleSet'][rule](state, _.get(args, 'props'));
  if (isValid) {
    _.each(dataPaths, (dataPath) => {
      _.unset(state, dataPath);
    });
  }
};

const toSetIf = (state, dataPath, args) => {
  const rule = _.get(args, 'rule');
  const isValid = RULES['evalRuleSet'][rule](state, _.get(args, 'props'));
  if (isValid) {
    toSet(state, dataPath, args);
  }
};

const toSetCommitmentToPreviousAwardAmountIf = (state, dataPath, args) => {
  let destField = _.get(args, 'destField');
  let sourceField = _.get(args, 'sourceField');
  let destArray = _.cloneDeep(_.get(state, dataPath));

  const rules = _.get(args, 'rules', []);
  const isValid = _.every(
    _.map(rules, (rule) => {
      return RULES['evalRuleSet'][_.get(rule, 'rule')](state, rule);
    })
  );

  if (isValid) {
    let changed = false;
    for (let i = 0; i < destArray.length; i++) {
      if (destArray[i]['state'] == null) {
        destArray[i][destField] = destArray[i][sourceField];
        destArray[i][sourceField] = 0;
        changed = true;
        destArray[i]['state'] = COMMITMENT_STATE.EXISTING;
      }
    }
    if (changed) {
      _.set(state, dataPath, destArray);
    }
  }
};

const toSetActualBudgetShare = (state, dataPath, args) => {
  const grantBudget = _.cloneDeep(_.get(state, args.dependencies[0]));
  const foConfig = _.cloneDeep(_.get(state, args.dependencies[1]));
  const fundingStreams = _.cloneDeep(_.get(state, args.dependencies[2]));
  const reductions = _.cloneDeep(_.get(state, args.dependencies[3]));
  const nonFederal = _.cloneDeep(_.get(state, args.dependencies[4]));
  const waiverRequested = _.cloneDeep(_.get(state, args.dependencies[5]));
  const costShareInformation = _.cloneDeep(_.get(state, args.dependencies[6]));
  const {calculateFields} = args;

  const share = calcCloseoutReportBudgetShare({
    grantBudget,
    foConfig,
    fundingStreams,
    reductions,
    nonFederal,
    calculateFields,
    waiverRequested,
    costShareInformation
  });
  _.set(state, dataPath, share);
};

const toSetFundingStreamDefaultActualValuesIf = (state, dataPath, args) => {
  let fieldMap = _.get(args, 'fieldMap', {});
  let destFundingStreams = _.cloneDeep(_.get(state, dataPath));

  const rules = _.get(args, 'rules', []);
  const isValid = _.every(
    _.map(rules, (rule) => {
      return RULES['evalRuleSet'][_.get(rule, 'rule')](state, rule);
    })
  );

  if (isValid) {
    let changed = false;
    destFundingStreams = _.map(destFundingStreams, (fundingStreamItem) => {
      let {
        fundingStreamItem: updatedFundingStreamItem,
        changed: fundingStreamChanged,
      } = setNestedCostItems(fundingStreamItem, fieldMap);
      changed = changed || fundingStreamChanged;
      return updatedFundingStreamItem;
    });
    if (changed) {
      _.set(state, dataPath, destFundingStreams);
    }
  }
};

const toSetIfMultipleRules = (state, dataPath, args) => {
  const rules = _.get(args, 'rules', []);
  const isValid = _.every(
    _.map(rules, (rule) => {
      return RULES['evalRuleSet'][_.get(rule, 'rule')](state, rule);
    })
  );
  if (isValid) {
    toSet(state, dataPath, args);
  }
};

const setNestedCostItems = (fundingStreamItem, fieldMap) => {
  let changed = false;
  if (fundingStreamItem.children) {
    let updatedChildren = _.cloneDeep(fundingStreamItem.children);
    _.forEach(fundingStreamItem.children, (child, key) => {
      let {fundingStreamItem: updatedChild, changed: childChanged} = setNestedCostItems(
        child,
        fieldMap
      );
      changed = changed || childChanged;
      if (childChanged) _.set(updatedChildren, key, updatedChild);
    });
    _.set(fundingStreamItem, 'children', updatedChildren);
  } else if (fundingStreamItem.cost) {
    const costArray = fundingStreamItem.cost;
    const updatedArray = _.map(costArray, (costItems) => {
      return _.map(costItems, (costItem) => {
        const updatedCostItem = _.cloneDeep(costItem);
        _.forEach(fieldMap, (destVal, sourceVal) => {
          if (
            updatedCostItem[sourceVal] !== undefined &&
            updatedCostItem[destVal] === undefined
          ) {
            _.set(updatedCostItem, destVal, updatedCostItem[sourceVal]);
            changed = true;
          }
        });
        return updatedCostItem;
      });
    });
    _.set(fundingStreamItem, 'cost', updatedArray);
  }
  return {fundingStreamItem, changed};
};

const matchAll = (...args) => true;

const skipOnLoad = (...args) => !_.get(args[3], 'onLoad', false);

const toEqual = (...args) => _.isEqual(args[0], args[1]);

const toNumberNotEqual = (...args) =>
  !_.isEqual(_.toNumber(args[0]), _.toNumber(args[1]));

const statutoryWaiverCancelled = (...args) => {
  const modifiedStatutoryData = args[0];
  const cancelledWaivers = _.filter(modifiedStatutoryData, (item) => {
    return item.waiverRequested === false;
  });
  return _.size(cancelledWaivers) > 0;
};

const subtractAndSet = (state, dataPath, args) => {
  const values = _.map(args, (arg) => _.get(state, arg, 0));
  const value = safeSubstract(...values);
  _.set(state, dataPath, value);
};

const resetAndsubtractAndSet = (state, dataPath, args, prevData) => {
  const values = _.map(args, (arg) => _.get(state, arg, 0));
  const oldValue = _.toNumber(prevData) > 0 ? _.toNumber(prevData) : 0;
  const value = safeAdd(safeSubstract(...values), oldValue);
  _.set(state, dataPath, value);
};

const unSet = (state, dataPath, args) => _.unset(state, dataPath);

const toSet = (state, dataPath, args) => {
  let setValue;
  if (_.includes(_.keys(args), 'setToTemplate')) {
    let props = {};
    _.forEach(_.get(args, 'dependencies'), (dep) => {
      _.forEach(dep, (depPath, depName) => {
        _.set(props, depName, _.get(state, depPath));
      });
    });
    setValue = compileContent(_.get(args, 'setToTemplate'), props);
  } else if (_.includes(_.keys(args), 'setToPath')) {
    setValue = _.get(state, _.get(args, 'setToPath'));
  } else {
    setValue = _.get(args, 'setToValue');
  }

  _.set(state, dataPath, setValue);
};

const delAttachment = (state, dataPath, args) =>
  RULES['evalRuleSet']['delAttachments'](state, dataPath);

const delAttachmentsAndUnset = (state, dataPath, args) =>
  RULES['evalRuleSet']['delAttachmentsAndUnset'](state, dataPath);

const delAttachmentIf = (state, dataPath, args) => {
  const rule = _.get(args, 'rule');
  const isValid = RULES['evalRuleSet'][rule](state, _.get(args, 'props'));
  if (isValid) {
    delAttachment(state, dataPath, args);
  }
};

const toSetByRule = (state, dataPath, args) => {
  const rule = _.get(args, 'rule');
  const value = RULES['evalRuleSet'][rule](state, _.get(args, 'props'), dataPath);
  _.set(state, dataPath, value);
};

const toSetByDynamicRule = (state, dataPath, args) => {
  const rule = _.get(args, 'rule');
  const value = RULES['evalRuleSet'][rule](state, _.omit(args, 'rule'), dataPath);
  _.set(state, dataPath, value);
};

const formatLatitude = (state, dataPath) => {
  let val = _.get(state, dataPath);
  const valSign = Math.sign(val);
  val = Math.abs(val).toFixed(6);
  if (Math.abs(val) < 10) val = `0${val}`;
  if (valSign === -1) val = `-${val}`;
  if (isNaN(valSign)) return;
  _.set(state, dataPath, val);
};

const formatLongitude = (state, dataPath) => {
  let val = _.get(state, dataPath);
  const valSign = Math.sign(val);
  val = Math.abs(val).toFixed(6);
  if (Math.abs(val) < 10) val = `00${val}`;
  else if (Math.abs(val) < 100) val = `0${val}`;
  if (valSign === -1) val = `-${val}`;
  if (isNaN(valSign)) return;
  _.set(state, dataPath, val);
};

const generateBudgetSummary = (state, dataPath, args) => {
  const {
    bocs,
    fundingStreamsPath,
    budgetPath,
    foConfigPath,
    calcTpc,
    foHandleProgIncome,
    managementCostItemCodes,
    costCalculation,
  } = args;
  const fundingStreams = _.get(state, fundingStreamsPath);
  const budget = _.get(state, budgetPath);
  const foConfig = _.get(state, foConfigPath);

  if (bocs && fundingStreams && budget && foConfig) {
    let numberOfPeriods = getNumberofPeriods(foConfig, fundingStreams);

    const constructionProgramIncome = _.get(
      budget,
      'nonFederal.constructionProgramIncome'
    );
    const programIncome = _.get(budget, 'nonFederal.programIncome');

    const budgetSummary = computeBudgetSummary(
      bocs,
      fundingStreams,
      numberOfPeriods,
      constructionProgramIncome,
      managementCostItemCodes,
      programIncome,
      foHandleProgIncome,
      calcTpc,
      costCalculation
    );

    _.set(state, dataPath, budgetSummary);
  }
};

const getNumberofPeriods = (foConfig, fundingStreams) => {
  let configFundingStreams = [];
  _.forEach(fundingStreams, (fundingStream) => {
    let index = _.findIndex(foConfig, (configFundingStream) => {
      return configFundingStream.fundingStreamType == fundingStream.fundingStreamType;
    });

    configFundingStreams.push(foConfig[index]);
  });
  const configFundingStream = _.reduce(
    configFundingStreams,
    (currentFundingStream, nextFundingStream) => {
      let currentApplicantShare = _.get(currentFundingStream, 'applicantCostShareRate');
      let nextApplicantShare = _.get(nextFundingStream, 'applicantCostShareRate');

      return _.size(currentApplicantShare) > _.size(nextApplicantShare)
        ? currentFundingStream
        : nextFundingStream;
    }
  );

  let periodType = _.get(configFundingStream, 'effectiveTimePeriodType');
  let applicantCostShareRate = _.get(configFundingStream, 'applicantCostShareRate');

  return periodType == 'whole' ? 1 : _.size(applicantCostShareRate);
};

const initProgrammaticCloseoutReviewCategorizedValues = (state, dataPath, args) => {
  let fundingStreams = _.get(state, dataPath);
  if (!fundingStreams) return;
  for (const fundStream of fundingStreams) {
    const fundStreamType = _.get(fundStream, 'fundingStreamType');
    const children = _.get(fundStream, 'children');
    _.forEach(children, (child) => {
      updateItemWithCategorizedValues(child);
    });
  }
};

const updateItemWithCategorizedValues = (item) => {
  const itemCode = _.get(item, 'itemCode');
  const costs = _.get(item, 'cost', undefined);
  const additionalInformation = _.get(item, 'additionalInformation', undefined);
  const additionalInfoActualTotal = _.get(
    additionalInformation,
    'actualTotal',
    undefined
  );
  const childItems = _.get(item, 'children', undefined);
  const hasCosts = costs !== undefined;
  const hasChildren = childItems !== undefined;
  if (hasChildren) {
    _.forEach(childItems, (childItem) => {
      updateItemWithCategorizedValues(childItem);
    });
  }
  if (hasCosts) {
    if (additionalInfoActualTotal) {
      updateCategorizedValuesInAdditionalInfo(item);
    } else {
      updateCategorizedValuesInCost(item);
    }
  }
};

/*
 * (Re-)Initialize all categorizedSum, scopeOfWorkTotal, allowedExcessTotal, and disallowedTotal
 * values if any of them are undefined in the additionalInformation section of the item.
 */
const updateCategorizedValuesInAdditionalInfo = (item) => {
  const additionalInfo = _.get(item, 'additionalInformation', undefined);
  if (!additionalInfo) return;
  const actualTotalArray = _.get(additionalInfo, 'actualTotal', undefined);
  if (!(actualTotalArray && actualTotalArray instanceof Array)) return;

  let scopeOfWorkTotalArray = _.get(additionalInfo, 'scopeOfWorkTotal', undefined);
  if (!scopeOfWorkTotalArray) {
    scopeOfWorkTotalArray = [];
    _.set(additionalInfo, 'scopeOfWorkTotal', scopeOfWorkTotalArray);
  }
  let allowedExcessTotalArray = _.get(additionalInfo, 'allowedExcessTotal', undefined);
  if (!allowedExcessTotalArray) {
    allowedExcessTotalArray = [];
    _.set(additionalInfo, 'allowedExcessTotal', allowedExcessTotalArray);
  }
  let disallowedTotalArray = _.get(additionalInfo, 'disallowedTotal', undefined);
  if (!disallowedTotalArray) {
    disallowedTotalArray = [];
    _.set(additionalInfo, 'disallowedTotal', disallowedTotalArray);
  }
  let categorizedSumArray = _.get(additionalInfo, 'categorizedSum', undefined);
  if (!categorizedSumArray) {
    categorizedSumArray = [];
    _.set(additionalInfo, 'categorizedSum', categorizedSumArray);
  }
  let scopeOfWorkTotalSum = 0;
  let allowedExcessTotalSum = 0;
  let disallowedTotalSum = 0;
  let categorizedSumSum = 0;
  _.forEach(actualTotalArray, (actualTotal, idx) => {
    let scopeOfWorkTotal = scopeOfWorkTotalArray[idx];
    let allowedExcessTotal = allowedExcessTotalArray[idx];
    let disallowedTotal = disallowedTotalArray[idx];
    let categorizedSum = categorizedSumArray[idx];
    if (!scopeOfWorkTotal || !allowedExcessTotal || !disallowedTotal || !categorizedSum) {
      scopeOfWorkTotalArray[idx] = actualTotal;
      allowedExcessTotalArray[idx] = 0;
      disallowedTotalArray[idx] = 0;
      categorizedSumArray[idx] = actualTotal;
    }
    scopeOfWorkTotalSum = safeAdd(scopeOfWorkTotalSum, scopeOfWorkTotalArray[idx]);
    allowedExcessTotalSum = safeAdd(allowedExcessTotalSum, allowedExcessTotalArray[idx]);
    disallowedTotalSum = safeAdd(disallowedTotalSum, disallowedTotalArray[idx]);
    categorizedSumSum = safeAdd(categorizedSumSum, categorizedSumArray[idx]);
  });
  _.set(additionalInfo, 'scopeOfWorkTotalSum', scopeOfWorkTotalSum);
  _.set(additionalInfo, 'allowedExcessTotalSum', allowedExcessTotalSum);
  _.set(additionalInfo, 'disallowedTotalSum', disallowedTotalSum);
  _.set(additionalInfo, 'categorizedSumSum', categorizedSumSum);
};

/*
 * (Re-)Initialize all categorizedSum, scopeOfWorkTotal, allowedExcessTotal, and disallowedTotal
 * values if any of them are undefined in the cost objects of the item.
 */
const updateCategorizedValuesInCost = (item) => {
  let costArrayOfArrays = _.get(item, 'cost', undefined);
  _.forEach(costArrayOfArrays, (costArray) => {
    _.forEach(costArray, (costObj) => {
      const actualTotal = _.get(costObj, ACTUAL_TOTAL, 0);
      const categorizedSum = _.get(costObj, CATEGORIZED_SUM, undefined);
      const scopeOfWorkTotal = _.get(costObj, SCOPE_OF_WORK_TOTAL, undefined);
      const allowedExcessTotal = _.get(costObj, ALLOWED_EXCESS_TOTAL, undefined);
      const disallowedTotal = _.get(costObj, DISALLOWED_TOTAL, undefined);

      if (
        categorizedSum === undefined ||
        scopeOfWorkTotal === undefined ||
        allowedExcessTotal === undefined ||
        disallowedTotal === undefined
      ) {
        _.set(costObj, CATEGORIZED_SUM, actualTotal);
        _.set(costObj, SCOPE_OF_WORK_TOTAL, actualTotal);
        _.set(costObj, ALLOWED_EXCESS_TOTAL, 0);
        _.set(costObj, DISALLOWED_TOTAL, 0);
      }
    });
  });
};

const triggerOperations = new Map();
triggerOperations.set('onLoad', matchAll);
triggerOperations.set('matchAll', matchAll);
triggerOperations.set('skipOnLoad', skipOnLoad);
triggerOperations.set('toEqual', toEqual);
triggerOperations.set('toNumberNotEqual', toNumberNotEqual);
triggerOperations.set('statutoryWaiverCancelled', statutoryWaiverCancelled);

const actionOperations = new Map();
actionOperations.set('toSet', toSet);
actionOperations.set('unSet', unSet);
actionOperations.set('unSetIf', unSetIf);
actionOperations.set('unSetMultipleIf', unSetMultipleIf);
actionOperations.set('toSetIf', toSetIf);
actionOperations.set('toSetIfMultipleRules', toSetIfMultipleRules);
actionOperations.set('toSetByRule', toSetByRule);
actionOperations.set('toSetByDynamicRule', toSetByDynamicRule);

actionOperations.set('resetAndsubtractAndSet', resetAndsubtractAndSet);
actionOperations.set('subtractAndSet', subtractAndSet);
actionOperations.set('delAttachments', delAttachment);
actionOperations.set('delAttachmentsAndUnset', delAttachmentsAndUnset);
actionOperations.set('formatLatitude', formatLatitude);
actionOperations.set('formatLongitude', formatLongitude);
actionOperations.set('delAttachmentIf', delAttachmentIf);
actionOperations.set(
  'initProgrammaticCloseoutReviewCategorizedValues',
  initProgrammaticCloseoutReviewCategorizedValues
);
actionOperations.set(
  'toSetCommitmentToPreviousAwardAmountIf',
  toSetCommitmentToPreviousAwardAmountIf
);
actionOperations.set(
  'toSetFundingStreamDefaultActualValuesIf',
  toSetFundingStreamDefaultActualValuesIf
);
actionOperations.set('toSetActualBudgetShare', toSetActualBudgetShare);

actionOperations.set('generateBudgetSummary', generateBudgetSummary);

//TODO:  Need to be able to chain rules.  Iterate through rules until you get a loop where no changes are made.  Identify and prevent cycles.
const applyRules = (state, action, extraProps, prevData) => {
  const paths = action.paths;
  const actionOperation = actionOperations.get(action.operation);
  paths.map((pathObj) => {
    const path = _.get(
      pathObj,
      'path',
      compileContent(_.get(pathObj, 'template'), extraProps)
    );
    if (pathObj.exclude) {
      const obj = _.get(state, path);
      const keys = _.differenceWith(
        _.keys(obj),
        pathObj.exclude,
        (key, excludeKey) => key === excludeKey
      );
      keys.map((key) => actionOperation(state, `${path}.${key}`));
    } else {
      const haveArgs = _.get(pathObj, 'deepClone', false);
      //Need to deep clone due to arguments is returning as Proxy.
      if (haveArgs) {
        const jsonObj = JSON.parse(JSON.stringify(pathObj));
        let args = _.get(jsonObj, 'arguments');
        if (jsonObj.dynamic) {
          args.extraProps = extraProps;
        }
        actionOperation(state, path, _.get(jsonObj, 'arguments'), prevData);
      } else {
        actionOperation(state, path);
      }
    }
  });
};

export const evalDependentDataRules = (
  originalState,
  dataPath,
  extraProps,
  prevData,
  reducers
) => {
  const reducersToUse =
    reducers ||
    _.get(
      originalState,
      'config.reducers',
      _.get(originalState, 'config.payload.reducers', []) // payload in config is also expected
    );

  // No reducers exist or data is not changed
  if (!reducersToUse || _.isEqual(_.get(originalState, dataPath), prevData)) {
    return originalState;
  }

  const reducerConfigs = _.filter(reducersToUse, (reducer) => {
    return (
      _.isEqual(
        // either trigger this by a reducer change
        dataPath,
        _.get(
          reducer,
          'trigger.dataPath',
          compileContent(_.get(reducer, 'trigger.template'), extraProps)
        )
      ) || _.isEqual('onLoad', _.get(reducer, 'trigger.match')) // or by the 'onLoad' flag
    );
  });

  if (reducerConfigs) {
    _.each(reducerConfigs, (reducerConfig) => {
      const triggerOperation = triggerOperations.get(reducerConfig.trigger.match);
      const data = _.get(originalState, dataPath);
      if (
        triggerOperation(data, prevData, _.get(reducerConfig, 'trigger.args'), extraProps)
      ) {
        _.map(_.get(reducerConfig, 'actions'), (action) =>
          applyRules(originalState, action, extraProps, prevData)
        );
        return originalState;
      }
    });
  }
  return originalState;
};
